'use strict';

const NO_OP_FN = function () {};

import BamArgValidator from 'bam-arg-validator';

/**
 * "Private" static functions used by BamTeamData and subclasses.  Other consumers should not call these methods.
 * Methods in this class which should be exposed publicly should be called from BamTeamData (as
 * getTeamArrayFromReqOrWindow() is).
 */
class Helpers {

    /**
     * Enhance teams array in various ways which are generic enough to be handled in the BamTeamData base class.
     * @param teams
     * @param addSlugFn
     */
    static extendTeams(teams, addSlugFn) {
        if (addSlugFn) {
            let team;
            for (let i = 0, ln = teams.length; i < ln; i++) {
                team = teams[i];
                addSlugFn(team);
            }
        }
    }

    /**
     * Static method which, passed either a BANE-controller req or a BANE browser window, returns an array of teams
     * in statsapi format (see e.g. http://qa-statsapi.mlb.com/api/v1/teams/?sportId=1).  Returns null if a team array
     * could not be found.
     * @param ctx A BANE controller request or browser window
     * @param clientArrAsDotNotatedStr e.g. "team_info" if arr is window.team_info
     * @param serverArrAsDotNotatedStr e.g. "path.to.teamArr" if arr is req.path.to.teamArr
     * @return {Array} A copy of the array found in ctx, or null
     */
    static getTeamArrayFromReqOrWindow(ctx, clientArrAsDotNotatedStr, serverArrAsDotNotatedStr) {
        if (!clientArrAsDotNotatedStr || !serverArrAsDotNotatedStr) {
            throw new Error('clientArrAsDotNotatedStr and serverArrAsDotNotatedStr are required.');
        }
        let teams = null;

        if (ctx.navigator) {
            // ctx is a browser window
            teams = Helpers._getObjFromDotNotatedStr(ctx, clientArrAsDotNotatedStr); // ctx.team_info;
        } else if (ctx.configFinder) {
            // ctx is a BANE controller
            try {
                teams = Helpers._getObjFromDotNotatedStr(ctx,
                    serverArrAsDotNotatedStr); // ctx.res.locals.teamInfo.teams, e.g. (for NHL)
            } catch (e) {}
        }

        if (!teams) {
            return null;
        }

        return Helpers.cloneDeep(teams);
    }

    /**
     * Passed an object and e.g. "some.path.to.an.object" as dotNotatedStr, returns obj.some.path.to.an.object
     * @param obj
     * @param dotNotatedStr
     * @returns {object}
     * @private
     */
    static _getObjFromDotNotatedStr(obj, dotNotatedStr) {
        let els = dotNotatedStr.split('.');
        let currentObj = obj;
        let el;
        for (let i = 0, ln = els.length; i < ln; i++) {
            el = els[i];
            currentObj = currentObj[el];
        }
        return currentObj;
    }

    /**
     * Expose cloneDeep()
     * @param arg
     */
    static cloneDeep(arg) {
        return cloneDeep(arg);
    }

    /**
     * Validates options passed to the BamTeamData constructor, throws an error if invalid.
     * @param candidateOptions The options object passed to the constructor
     * @param supportedOptionSpecs SUPPORTED_OPTION_SPECS in BamTeamData
     */
    static validateOptions(candidateOptions, supportedOptionSpecs) {
        // Unspecified options and passing null are both allowed:
        if (candidateOptions !== undefined && candidateOptions !== null) {
            let errMsg = null;

            if (typeof candidateOptions !== 'object' || Array.isArray(candidateOptions)) {
                errMsg = 'is not an object.';
            }
            if (!errMsg) {
                let optionKeys = Object.keys(candidateOptions);
                let optionSpec;
                let optionValue;
                let optionValueType;
                let optionKey;
                for (let i = 0, ln = optionKeys.length; i < ln; i++) {
                    optionKey = optionKeys[i];
                    optionSpec = supportedOptionSpecs[optionKey];
                    if (optionSpec === undefined) {
                        errMsg = 'contains unsupported option: ' + optionKey;
                        break;
                    }
                    optionValue = candidateOptions[optionKey];
                    optionValueType = (Array.isArray(optionValue) ? 'array' : typeof optionValue);
                    if (optionValue !== null) {
                        if (optionValueType !== optionSpec.expectedType) {
                            errMsg = 'contains option with unexpected value type for "' + optionKey +
                                '" (expected: ' + optionSpec.expectedType + '; found: ' + optionValueType + ')';
                            break;
                        }
                    }
                }
            }

            if (errMsg) {
                throw new Error('The options arg you passed ' + errMsg);
            }
        }
    }

    /**
     * Methods should call this if the base class doesn't implement the method, relying on subclasses to do so.
     * @param methodName The name of the unimplemented method, for the Error message
     * @throws Error when called.
     */
    static throwNotImplementedError(methodName) {
        throw new Error(methodName + '() is not implemented in the BamTeamData, and the subclass has not provided' +
            'an implementation either.');
    }


    /**
     * Validates the activeTeamsArr and inactiveTeamsArr args passed to the BamTeamData constructor.
     * @param candidateArr The array passed to the constructor
     * @param argName "activeTeamsArr" or "inactiveTeamsArr"
     * @param inactivesAllowedInActiveArr True if inactive teams are allowed in the active-teams array (e.g., since
     *  the NHL Vegas team has never played a game, it's inactive (which makes it different from typical inactive
     *  teams, which are inactive because they are historic).
     * @param activeTeams Boolean; true if these are active teams, false if inactive
     */
    static validateStatsapiTeamsArr(candidateArr, argName, inactivesAllowedInActiveArr, activeTeams) {
        let errMsg = null;
        if (!Array.isArray(candidateArr)) {
            errMsg = 'is not an array';
        }
        if (!errMsg && activeTeams && candidateArr.length === 0) {
            errMsg = 'contains no elements';
        }
        if (!errMsg && (candidateArr.length > 0)) {
            try {
                // Don't let it throw if invalid; catch and give user a more meaningful error message:
                BamArgValidator.validateStatsapiTeam(candidateArr[0]);
            } catch (e) {
                errMsg = 'is an array but its elements are not in statsapi-team format.';
            }
        }
        if (!errMsg) {
            let team;
            for (let i = 0, ln = candidateArr.length; i < ln; i++) {
                team = candidateArr[i];
                if ((!inactivesAllowedInActiveArr || !activeTeams) && team.active !== activeTeams) {
                    errMsg = 'contains ' + (activeTeams ? 'in' : '') + 'active teams.';
                    break;
                }
            }
        }

        if (errMsg) {
            throw new Error(argName + ' ' + errMsg);
        }
    }
}


/**
 * Returns a deep copy of any value. This function calls itself recursively when the passed value is not an object or
 * an array.  Functions are not cloned; they are returned by reference.
 * @param {*} val
 * @return {*}
 */
function cloneDeep(val) {
    let valType = typeof val;

    // PRIMITIVES:
    if (val === null || (['undefined', 'boolean', 'number', 'string', 'symbol'].indexOf(valType) > -1)) {
        // just return the literal value (note: typeof null is "object"):
        return val;
    }

    // FUNCTION:
    if (valType === 'function') {
        return val; // return as if a primitive; sometime we can actually figure out how to clone functions
    }

    // ARRAY:
    if (Array.isArray(val)) {
        // Type is "object" but this is an array
        let arr = [];
        let el;
        for (let i = 0, ln = val.length; i < ln; i++) {
            el = val[i];
            arr.push(cloneDeep(el));
        }
        return arr;
    }

    // OBJECT:
    let obj = {};
    let keys = Object.keys(val);
    let key;
    for (let i = 0, ln = keys.length; i < ln; i++) {
        key = keys[i];
        obj[key] = cloneDeep(val[key]);
    }
    return obj;
}



export default Helpers;
